import React, {useState, useRef, useEffect} from 'react';
import {
  TextField,
  ClickAwayListener,
  Popper,
  Paper,
  styled,
} from '@mui/material';
import {
  Button,
  designSystemToken,
  Icon,
  designSystemComposition,
} from '@lightricks/react-design-system';
import elevationShadow from '@/utils/elevationShadow';
import translate from '@/utils/translate';

interface TextFieldPopperProps {
  testID?: string;
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onSubmit: (value: string) => void;
  initialValue?: string;
  placeholder?: string;
  controlled?: boolean;
  placement?:
    | 'bottom-start'
    | 'bottom-end'
    | 'top-start'
    | 'top-end'
    | 'bottom'
    | 'top'
    | 'left'
    | 'right';
}

const PaperContainer = styled(Paper)(() => ({
  backgroundColor: designSystemToken('semantic.bg.secondary'),
  borderRadius: '16px',
  padding: '8px',
  boxShadow: elevationShadow(2),
  minWidth: '280px',
  maxWidth: '300px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '4px',
}));

const StyledTextField = styled(TextField)(() => ({
  'flex': 1,
  '& .MuiOutlinedInput-root': {
    'borderRadius': '0px',
    'border': 'none',
    'backgroundColor': 'transparent',
    'padding': '12px 16px',
    '&:hover': {
      border: 'none',
    },
    '&.Mui-focused': {
      border: 'none',
    },
  },
  '& .MuiInputBase-input': {
    padding: '0',
    ...designSystemComposition('typography.body.lg'),
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));

const ActionButton = styled(Button)(() => ({
  'minWidth': 'auto',
  'height': '32px',
  'width': '32px',
  'borderRadius': '50%',
  'padding': '0',
  'display': 'flex',
  'alignItems': 'center',
  'justifyContent': 'center',
  'backgroundColor': designSystemToken('semantic.bg.neutral-inverse'),
  '&:hover': {
    backgroundColor: designSystemToken('semantic.bg.neutral-inverse'),
  },
  '&:disabled': {
    backgroundColor: designSystemToken('semantic.bg.neutral-inverse'),
  },
}));

function TextFieldPopper({
  testID = 'text-field-popper',
  open,
  anchorEl,
  onClose,
  onSubmit,
  initialValue = '',
  placeholder = translate('Enter text...'),
  controlled = false,
  placement = 'bottom-start',
}: TextFieldPopperProps) {
  const [value, setValue] = useState(initialValue);
  const textFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (open && textFieldRef.current) {
      timeout = setTimeout(() => {
        textFieldRef.current?.focus();
      }, 100);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [open]);

  useEffect(() => {
    if (controlled) {
      setValue(initialValue);
    }
  }, [initialValue, controlled]);

  const handleSubmit = () => {
    const trimmedValue = value.trim();
    if (trimmedValue) {
      onSubmit(trimmedValue);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSubmit();
    } else if (event.key === 'Escape') {
      onClose();
    }
  };

  const canSubmit = Boolean(value.trim());

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement={placement}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 8],
          },
        },
      ]}
    >
      <ClickAwayListener onClickAway={onClose}>
        <PaperContainer data-testid={testID}>
          <StyledTextField
            ref={textFieldRef}
            placeholder={placeholder}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyPress}
            fullWidth
            variant="outlined"
            autoFocus
          />

          <ActionButton
            appearance="neutral"
            size="small"
            mode="filled"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            <Icon
              name="Actions-Accept"
              size="small"
              appearance="neutral"
              color={designSystemToken('semantic.fg.primary')}
            />
          </ActionButton>
        </PaperContainer>
      </ClickAwayListener>
    </Popper>
  );
}

export default TextFieldPopper;
